import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import { BehaviorSubject, combineLatest, fromEvent, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { isEqual } from 'lodash-es';
import isBoolean from 'lodash-es/isBoolean';
import { DroppanelStyle } from '../../../../elements/components/droppanel/entities/droppanel-style.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { BaseControlValueAccessor, MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { CheckboxSelectionFormValues } from '../entities/checkbox-selection-value.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import { ShIndeterminateDirective } from '../../../directives/indeterminate.directive';
import { NgClass, AsyncPipe } from '@angular/common';
import { ShDroppanelSelectComponent } from '../../../../elements/components/droppanel-select/droppanel-select.component';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';

@Component({
    selector: 'sh-form-checkbox-with-dropdown-complex-base',
    templateUrl: './form-checkbox-with-dropdown-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormCheckboxWithDropdownComplexBaseComponent)],
    standalone: true,
    imports: [ShClickOutsideDirective, ShDroppanelSelectComponent, NgClass, ReactiveFormsModule, ShIndeterminateDirective, ShIconComponent, AsyncPipe]
})
export class ShFormCheckboxWithDropdownComplexBaseComponent<T = string>
    extends BaseControlValueAccessor
    implements OnInit, OnDestroy, AfterViewInit
{
    public checkControl: UntypedFormControl;
    public selectControl: UntypedFormControl = new UntypedFormControl();
    public droppanelStyle: DroppanelStyle = DroppanelStyle.Default;
    public sizes: typeof IconSize = IconSize;

    readonly selectedValues$: ReplaySubject<FormValue<T>[]> = new ReplaySubject<FormValue<T>[]>(1);
    readonly selectOptions$: ReplaySubject<MultiFormSelectOptions<T>> = new ReplaySubject<MultiFormSelectOptions<T>>(1);
    readonly selectionState$: BehaviorSubject<FormValue<T>[]> = new BehaviorSubject<FormValue<T>[]>([]);
    readonly isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly indeterminate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input() id!: string;
    @Input() invalid = false;
    @Input() describeLabelId = '';
    @Input() label = '';
    @Input() inverted = false;
    /**
     * Renders the panel `position: fixed` instead of absolute, for a host inside a scroll container that would
     * otherwise clip it. Forwarded to `sh-droppanel-select`, which already carried it.
     */
    @Input() inOverflownContainer = false;
    @Input() set options(value: FormSelectOption<T>[]) {
        if (value) {
            this.selectOptions$.next({
                isLiveData: false,
                options: value
            });
        }
    }

    @ViewChild('checkboxEl') checkboxEl!: ElementRef;

    constructor(changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);

        this.checkControl = new UntypedFormControl();
        this.selectControl = new UntypedFormControl();
    }

    ngOnInit(): void {
        this.initPropagation();
    }

    ngAfterViewInit(): void {
        this.initCheckboxListener();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.indeterminate$.complete();
        this.selectOptions$.complete();
        this.selectedValues$.complete();
        this.selectionState$.complete();
        this.isOpen$.complete();
    }

    writeValue(obj: CheckboxSelectionFormValues<T>): void {
        if (obj === null) {
            this.unset();
            this.selectedValues$.next([]);
            return;
        }

        if (obj.checkbox) {
            switch (obj.checkbox.state) {
                case FormValueState.SET:
                    this.set(obj.checkbox.value);
                    break;
                case FormValueState.INDETERMINATE:
                    this.makeIndeterminate();
                    break;
                case FormValueState.UNSET:
                    this.unset();
                    break;
                default:
                    this.unset();
            }
        } else {
            this.unset();
        }

        if (obj.selection) {
            this.selectedValues$.next(obj.selection);
        } else {
            this.selectedValues$.next([]);
        }
    }

    initPropagation(): void {
        const selection$: Observable<FormValue<T>[]> = this.selectionState$;
        const checkbox$: Observable<boolean> = this.checkControl.valueChanges.pipe(distinctUntilChanged(isEqual));

        combineLatest([checkbox$, selection$, this.indeterminate$])
            .pipe(
                map(([checkboxFormValue, selectedFormValues, indeterminate]) => {
                    if (indeterminate) {
                        return {
                            checkbox: new FormValue(false, FormValueState.INDETERMINATE),
                            selection: selectedFormValues ?? []
                        };
                    }

                    if (checkboxFormValue !== null) {
                        return {
                            checkbox: new FormValue(checkboxFormValue, FormValueState.SET),
                            selection: selectedFormValues ?? []
                        };
                    } else {
                        return {
                            checkbox: new FormValue(false, FormValueState.SET),
                            selection: selectedFormValues ?? []
                        };
                    }
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(changes => this.propagateChange(changes));
    }

    initCheckboxListener(): void {
        const changes$: Observable<any> = fromEvent(this.checkboxEl.nativeElement, 'change');

        changes$
            .pipe(takeUntil(this.destroy$))
            .pipe(filter(value => !!value))
            .subscribe(() => {
                // Close dropdown when checkbox is clicked or changes
                this.setDropdownClosed();

                if (this.indeterminate$.value === true) {
                    this.set(true);
                    this.indeterminate$.next(false);
                }
            });
    }

    public unset(): void {
        this.indeterminate$.next(false);

        this.checkControl.setValue(false);
    }

    public set(value: boolean | null): void {
        if (!isBoolean(value) || value === null) {
            console.error(
                'ShFormCheckboxWithDropdownComplexBaseComponent:: FormValue does not contain a boolean',
                value
            );

            this.unset();

            return;
        }

        this.indeterminate$.next(false);

        this.checkControl.setValue(!!value);
    }

    public makeIndeterminate(): void {
        this.indeterminate$.next(true);

        this.checkControl.setValue(false);
    }

    // #region Dropdown
    public toggleDropdown(event: Event): void {
        if (this.disabled) {
            return;
        }

        // Ignore checkbox clicks
        if (event.target instanceof HTMLInputElement && event.target.tagName === 'INPUT') {
            return;
        }
        this.isOpen$.next(!this.isOpen$.value);
    }

    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }
    // #endregion

    public onSelectionStateChange(formValueSelectOptions: FormValue<FormSelectOption<T>>[]): void {
        const selection: FormValue<T>[] = formValueSelectOptions.map(
            formValueSelectOption => new FormValue(formValueSelectOption.value.value, formValueSelectOption.state)
        );

        this.selectionState$.next(selection);
    }
}
